import { useEffect, useState } from 'react';
import { LogoLoader } from './LogoLoader/LogoLoader';

interface PageSuspenseFallbackProps {
  /** How long the page may take before a loader is worth showing. */
  delayMs?: number;
}

/**
 * Suspense fallback for a page's content area, so the sidebar and header stay
 * on screen while a page's code loads (the full-page `AppLoaderPage` replaces
 * the whole app and belongs to the first load). Renders nothing for the first
 * moments: a page that arrives within `delayMs` never shows a loader at all.
 */
export function PageSuspenseFallback({
  delayMs = 250,
}: PageSuspenseFallbackProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), delayMs);
    return () => clearTimeout(timer);
  }, [delayMs]);

  if (!visible) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      className="flex flex-1 items-center justify-center min-h-[40dvh]"
    >
      <LogoLoader size="md" color="brand" />
    </div>
  );
}
